import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';
import ImageViewer from '@/pages/sample/components/ImageViewer';
import { sampleCases } from '@/mocks/sampleData';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '시공사례 | 대구드레스룸 키친스',
    description: '대구드레스룸 키친스의 실제 드레스룸, 붙박이장, 팬트리 시공사례를 확인해보세요.',
    url: `${SITE_URL}/sample`,
    inLanguage: 'ko',
    isPartOf: {
      '@type': 'WebSite',
      name: '대구드레스룸 키친스',
      url: SITE_URL,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: '대구드레스룸 키친스',
    url: SITE_URL,
    telephone: '053-766-0411',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '침산남로48',
      addressLocality: '북구',
      addressRegion: '대구광역시',
      addressCountry: 'KR',
    },
  },
];

export default function SamplePage() {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = sampleCases.length;
  const currentCase = sampleCases[currentPage - 1];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SeoHead
        title="시공사례 | 대구드레스룸 키친스"
        description="대구드레스룸 키친스의 실제 드레스룸, 붙박이장, 팬트리 시공사례를 확인해보세요. 대구 북구 침산동 드레스룸 전문업체."
        keywords="드레스룸시공사례, 붙박이장시공, 팬트리시공, 대구드레스룸"
        canonical={`${SITE_URL}/sample`}
        jsonLd={jsonLd}
      />
      <Navbar />

      <main className="flex-1 pt-16">
        {/* Page Header */}
        <section className="border-b border-gray-100 py-8">
          <div className="max-w-6xl mx-auto px-6">
            <h1 className="text-xl font-semibold text-gray-800">시공사례</h1>
            <p className="text-sm text-gray-500 mt-1">
              대구드레스룸 키친스의 실제 시공 현장을 확인해보세요.
            </p>
          </div>
        </section>

        {/* Case Title */}
        {currentCase && (
          <section className="max-w-6xl mx-auto px-6 pt-8 pb-2">
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-full px-3 py-1">
                No.{String(currentPage).padStart(2, '0')}
              </span>
              <h2 className="text-base font-semibold text-gray-800">{currentCase.title}</h2>
            </div>
          </section>
        )}

        {/* Image Viewer */}
        <section className="max-w-6xl mx-auto px-6 py-6">
          {currentCase ? (
            <ImageViewer
              images={currentCase.images}
              thumbnails={currentCase.thumbnails}
            />
          ) : (
            <p className="text-center text-gray-400 py-20">해당 페이지의 시공사례가 없습니다.</p>
          )}
        </section>

        {/* Pagination */}
        <section className="max-w-6xl mx-auto px-6 pb-12">
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
              aria-label="이전 페이지"
            >
              <i className="ri-arrow-left-s-line" />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer ${
                  currentPage === page
                    ? 'bg-yellow-500 text-white'
                    : 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
              aria-label="다음 페이지"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>

          <p className="text-center text-xs text-gray-400 mt-3">
            {currentPage} / {totalPages} 페이지
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
